import ActionButton from '@repo/ui/action-button'
import { Column } from '@repo/ui/data-table'
import { PenIcon, PlusIcon, SettingIcon } from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import { useMutation } from '@tanstack/react-query'
import React from 'react'
import { toast } from 'sonner'

import { API } from '../../../api/apis.config'
import { createStaffCommentSubject } from '../../../api/staff-comment-subjects/create-staff-comment-subject'
import { updateStaffCommentSubject } from '../../../api/staff-comment-subjects/edit-staff-comment-subject'
import DynamicDataTable, { DynamicTableRef } from '../../../components/elements/dynamic-table'
import UserNavBar from '../../../components/elements/nav-bar'
import StaffCommentSubjectsForm from '../../../components/pages/staff-comment-subjects/staff-comment-subject-form'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import { StaffCommentSubject } from '../../../lib/types/models/staff-comment-subject/staff-comment-subject'
import { StaffCommentSubjectInput } from '../../../lib/types/models/staff-comment-subject/staff-comment-subject-input'
import { modalButtonConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'

const StaffCommentSubjectsPage = () => {
  const tableRef = React.useRef<DynamicTableRef<StaffCommentSubject>>(null)

  const { modalState, selectedData, toggleModal, closeModel } = useModalManager<
    'edit' | 'create',
    StaffCommentSubject
  >(['edit', 'create'])

  const { mutateAsync: mutateEdit } = useMutation({
    mutationFn: updateStaffCommentSubject,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.refetch()
      closeModel('edit')
      toast.success('Sujet modifié avec succès')
    }
  })

  const onSubmitEdit = async (values: StaffCommentSubjectInput) => {
    if (!selectedData) return
    await mutateEdit({ id: selectedData.id, ...values })
  }

  const { mutateAsync } = useMutation({
    mutationFn: createStaffCommentSubject,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.refetch()
      closeModel('create')
      toast.success('Sujet créé avec succès')
    }
  })

  const columns: Column<StaffCommentSubject>[] = [
    {
      accessorKey: 'id',
      header: 'Id',
      enableSorting: true,
      cell: (data) => data.row.original.id
    },
    {
      accessorKey: 'name',
      header: 'NOM',
      enableSorting: true
    },
    {
      accessorKey: 'id',
      header: '',
      cell: (data) => (
        <ActionButton
          variant={'grey'}
          icon={PenIcon}
          onClick={() => toggleModal('edit', data.row.original)}
        />
      )
    }
  ]

  return (
    <div>
      <UserNavBar title="Liste des sujets" icon={SettingIcon} />
      <div className="h-4" />
      <DynamicDataTable<StaffCommentSubject>
        ref={tableRef}
        title="Liste des sujets"
        deleteMessage={{
          name: 'sujets',
          attributeName: 'name',
          deleteOne: (subject) => `Êtes-vous sûr de vouloir supprimer le sujet "${subject.name}" ?`
        }}
        columns={columns}
        selectable
        allowSearchParam
        endpoint={API.staffCommentSubjects.getAll.url}
        actions={[
          {
            type: 'global',
            icon: PlusIcon,
            label: 'Créer',
            fn: () => toggleModal('create')
          }
        ]}
      />

      {/* Edit */}
      <Modal
        icon={SettingIcon}
        isOpen={modalState.edit}
        title="Modifier"
        hideActions
        onCancel={() => toggleModal('edit')}
      >
        <StaffCommentSubjectsForm
          className={MODAL_BODY_CLASSNAME}
          defaultValues={selectedData}
          buttonsConfig={modalButtonConfig({
            onCancel: () => closeModel('edit'),
            onConfirm: onSubmitEdit
          })}
        />
      </Modal>

      {/* Create */}
      <Modal
        icon={SettingIcon}
        isOpen={modalState.create}
        title="Créer"
        hideActions
        onCancel={() => toggleModal('create')}
      >
        <StaffCommentSubjectsForm
          className={MODAL_BODY_CLASSNAME}
          buttonsConfig={modalButtonConfig({
            onCancel: () => closeModel('create'),
            onConfirm: mutateAsync
          })}
        />
      </Modal>
    </div>
  )
}

export default StaffCommentSubjectsPage
